import { BeachChairResponseDto, BookingDetailsResponseDto, BookingResponseDto, BookingStatus, RatesQueryResponse } from "../../../api-client/generated";
import { publicApi } from "../../../api/api";
import { toast } from "react-toastify";
import i18next from "i18next";
import { startBookButtonLoading } from "../../../store/reducers/bookButtonReducer";
import { changeBeachChairDates, changeBeachChairDatesSelected, selectionModalRequest } from "../../../store/reducers/mainAppReducer";
import { selectBeachChairModelRateRequest } from "../../../store/reducers/availabilityReducer";
import { addBeachChairBookingRequest } from "../../../store/reducers/bookingReducer";
import { IPrice } from "../../../store/actionPayload/pricePayloads";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";

export type handleCheckDraftBookingResponse = {
    beachChairId: number;
    beachChairNumber: string;
    isAvailable: boolean,
};

export interface BookingDraft {
    booking: BookingResponseDto | BookingDetailsResponseDto;
    publicReference?: string;
    email?: string;
    expires: number;
    timestamp?: number;
}

export interface CompleteBooking{
    bookingRef: string,
    email: string,
    status: BookingStatus
}

const DRAFT_EXPIRY_TIME = 30 * 60 * 1000; // 30 minutes
const DRAFT_STORAGE_KEY = 'booking_draft';

const COMPLETED_BOOKING_STORAGE_KEY = "booking_complete";

/**
 * Save booking draft to localStorage
 */
export function saveDraftBooking(
    booking: BookingResponseDto | BookingDetailsResponseDto,
    publicReference?: string,
    email?: string
): void {
    try {
        const draft: BookingDraft = {
            booking,
            publicReference,
            email,
            expires: Date.now() + DRAFT_EXPIRY_TIME,
            timestamp: Date.now()
        };
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
    } catch (e) {
        toast.error(i18next.t('bookings:failedToSaveDraftBooking'));
    }
}

/**
 * Remove booking draft from localStorage
 */
export function removeCompletedBooking(): void {
    try {
        localStorage.removeItem(COMPLETED_BOOKING_STORAGE_KEY);
    } catch (e) {
        toast.error(i18next.t('bookings:failedToRemoveCompletedBooking'));
    }
}

/**
 * Remove booking draft from localStorage
 */
export function removeDraftBooking(): void {
    try {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
    } catch (e) {
        toast.error(i18next.t('bookings:failedToRemoveDraftBooking'));
    }
}

/**
 * Get booking draft from localStorage
 */
export function getCompleteBooking(): CompleteBooking | null {
    try {
        const completeBooking = localStorage.getItem(COMPLETED_BOOKING_STORAGE_KEY);
        if (!completeBooking) return null;
        return JSON.parse(completeBooking) as CompleteBooking;
    } catch (e) {
        toast.error(i18next.t('bookings:failedToGetCompletedBooking'));
        return null;
    }
}

/**
 * Get booking draft from localStorage
 */
export function getDraftBooking(): BookingDraft | null {
    try {
        const draftData = localStorage.getItem(DRAFT_STORAGE_KEY);
        if (!draftData) return null;
        return JSON.parse(draftData) as BookingDraft;
    } catch (e) {
        toast.error(i18next.t('bookings:failedToGetDraftBooking'));
        return null;
    }
}

export async function handleCheckDraftBooking(
    booking: BookingResponseDto | BookingDetailsResponseDto | undefined,
    bookingPublicReference: string,
): Promise<handleCheckDraftBookingResponse[]> {

    const availabilityResults: handleCheckDraftBookingResponse[] = [];

    if (booking?.beachChairBookings?.length) {
        const checkPromises = booking.beachChairBookings.map(async (chair: any) => {
            try {
                const { data } = await publicApi.publicControllerCheckBeachChairAvailability({
                    beachChairId: chair.beachChairId,
                    publicReference: bookingPublicReference,
                    start: chair.start?.split('T')[0] || '',
                    end: chair.end?.split('T')[0] || '',
                });

                return {
                    beachChairId: chair.beachChairId,
                    beachChairNumber: chair.publicNumber,
                    isAvailable: data.available ?? false,
                };
            } catch (error: any) {
                // 400 Bad Request or any error means the booking doesn't exist = available
                return {
                    beachChairId: chair.beachChairId,
                    beachChairNumber: chair.publicNumber,
                    isAvailable: true,
                };
            }
        });

        const results = await Promise.all(checkPromises);
        availabilityResults.push(...results);
    }

    return availabilityResults;
}

/**
 * Check if cart needs to sync with localStorage - Chainable API
 */
export function handleCheckCartSyncToLocalStorage(currentBooking: BookingResponseDto | BookingDetailsResponseDto | undefined) {
    const draftBooking = getDraftBooking();
    const isSyncable = (draftBooking: BookingDraft | null, currentBooking: BookingResponseDto | BookingDetailsResponseDto | undefined): boolean => {
        if (draftBooking === null && currentBooking === undefined) {
            return true;
        }
        if ((draftBooking === null && currentBooking !== undefined) || (draftBooking !== null && currentBooking === undefined)) {
            return false;
        }

        const draftChairs = draftBooking?.booking.beachChairBookings || [];
        const cartChairs = currentBooking?.beachChairBookings || [];

        // Check length
        if (draftChairs.length !== cartChairs.length) {
            return false;
        }

        // Create a Set of unique identifiers from cart for O(1) lookup
        const cartChairSet = new Set<string>();
        for (let i = 0; i < cartChairs.length; i++) {
            const chair = cartChairs[i];
            cartChairSet.add(`${chair.id}-${chair.publicNumber}`);
        }

        // Check if all draft chairs exist in cart
        for (let i = 0; i < draftChairs.length; i++) {
            const draftChair = draftChairs[i];
            const identifier = `${draftChair.id}-${draftChair.publicNumber}`;
            if (!cartChairSet.has(identifier)) {
                return false;
            }
        }

        return true;
    }

    return {
        onSync: (callback: () => void) => {
            if (isSyncable(draftBooking, currentBooking) === true) {
                callback();
            }
            return {
                onNoSync: (fallbackCallback: () => void) => {
                    if (isSyncable(draftBooking, currentBooking) === false) {
                        fallbackCallback();
                    }
                }
            };
        },
        onNoSync: (callback: () => void) => {
            if (isSyncable(draftBooking, currentBooking) === false) {
                callback();
            }
            return {
                onSync: (fallbackCallback: () => void) => {
                    if (isSyncable(draftBooking, currentBooking) === true) {
                        fallbackCallback();
                    }
                }
            };
        }
    };
}

export function handleAddToCartWithRateAndDiscount(rate: RatesQueryResponse, allowBook: boolean, selectedBeachChair: BeachChairResponseDto | undefined, dispatch: Dispatch<AnyAction>) {
    dispatch(startBookButtonLoading());
    dispatch(
        changeBeachChairDates({ rate: rate.type })
    );
    dispatch(
        selectBeachChairModelRateRequest({
            model: selectedBeachChair?.model || "",
            rate: rate.type,
            discount: rate.discount,
        })
    );
    if (allowBook) {
        dispatch(
            selectionModalRequest({ toggle: true })
        );
    } else {
        dispatch(
            addBeachChairBookingRequest({
                discount: rate.discount,
            })
        );
    }
}

export function handleAddToCartNormal(rate: IPrice, allowBook: boolean, selectedBeachChair: BeachChairResponseDto | undefined, dispatch: Dispatch<AnyAction>) {
    dispatch(startBookButtonLoading());
    dispatch(changeBeachChairDatesSelected({}));
    dispatch(
        selectBeachChairModelRateRequest({
            model: selectedBeachChair?.model || "",
            discount: rate.discount || false,
            rate: "none",
        })
    );
    if (allowBook) {
        dispatch(selectionModalRequest({ toggle: true }));
    } else {
        dispatch(
            addBeachChairBookingRequest({
                discount: rate.discount || false,
            })
        );
    }
}



